import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../../api/client";
import Toast from "../../components/Toast";
import ConfirmDialog from "../../../../components/common/ConfirmDialog";
import PermissionGate from "../../../../components/common/PermissionGate";
import { PERMISSIONS } from "../../../../constants/permissions";
import AssetModal, { Field } from "../assets/AssetModal";
import { errorMessage } from "../assets/assetUtils";
import "../assets/Assets.css";

const ITEM_TYPES = [
  { value: "check", label: "OK / Not OK" },
  { value: "number", label: "Reading" },
  { value: "text", label: "Written note" },
];

const blankItem = () => ({ id: "", label: "", type: "check", required: true });

// Building a maintenance checklist. Filled in the app only, when a PM is
// completed or maintenance is logged — never over chat.
function ChecklistModal({ checklist, onClose, onSaved }) {
  const editing = Boolean(checklist);
  const [name, setName] = useState(checklist?.name || "");
  const [description, setDescription] = useState(checklist?.description || "");
  const [items, setItems] = useState(() => (checklist?.items?.length ? checklist.items.map((i) => ({ ...i })) : [blankItem()]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const update = (index, patch) => setItems((list) => list.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  const move = (index, delta) =>
    setItems((list) => {
      const next = [...list];
      const target = index + delta;
      if (target < 0 || target >= next.length) return list;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { name, description, items: items.filter((i) => i.label.trim()) };
      const res = editing
        ? await api.put(`/manufacturing/assets/checklists/${checklist.id}`, body)
        : await api.post("/manufacturing/assets/checklists", body);
      onSaved(res.data.data);
    } catch (err) {
      setError(errorMessage(err, "Couldn't save the checklist."));
      setBusy(false);
    }
  };

  return (
    <AssetModal
      title={editing ? "Edit checklist" : "New maintenance checklist"}
      wide
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={editing ? "Save changes" : "Create checklist"}
    >
      <div className="am-row">
        <Field label="Name *">
          <input className="am-input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} placeholder="e.g. CNC weekly PM" />
        </Field>
        <Field label="Description">
          <input className="am-input" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} />
        </Field>
      </div>

      <div className="am-field">
        <span>Items</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((item, i) => (
            <div className="am-builder-item" key={i}>
              <input
                className="am-input"
                value={item.label}
                onChange={(e) => update(i, { label: e.target.value })}
                placeholder={`Item ${i + 1}, e.g. Check hydraulic oil level`}
                aria-label={`Item ${i + 1}`}
                maxLength={200}
              />
              <select className="am-select" value={item.type} onChange={(e) => update(i, { type: e.target.value })} aria-label="Answer type">
                {ITEM_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <label className="am-check" title="Must be answered to complete">
                <input type="checkbox" checked={item.required} onChange={(e) => update(i, { required: e.target.checked })} />
                Required
              </label>
              <span style={{ whiteSpace: "nowrap" }}>
                <button type="button" className="am-link-btn" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>{" "}
                <button type="button" className="am-link-btn" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down">↓</button>{" "}
                <button type="button" className="am-link-btn danger" onClick={() => setItems((list) => list.filter((_, j) => j !== i))} disabled={items.length === 1} aria-label="Remove item">✕</button>
              </span>
            </div>
          ))}
        </div>
        <div>
          <button type="button" className="am-btn am-btn-ghost am-btn-sm" onClick={() => setItems((list) => [...list, blankItem()])}>+ Add item</button>
        </div>
        <small>Checklists are filled in the app when maintenance is logged or a PM is completed. They are not sent on WhatsApp.</small>
      </div>
    </AssetModal>
  );
}

export default function AssetSettings() {
  const [checklists, setChecklists] = useState([]);
  const [people, setPeople] = useState([]);
  const [fallback, setFallback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingFallback, setSavingFallback] = useState(false);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cl, ppl, st] = await Promise.all([
        api.get("/manufacturing/assets/checklists"),
        api.get("/manufacturing/assets/people"),
        api.get("/manufacturing/assets/settings"),
      ]);
      setChecklists(cl.data.data);
      setPeople(ppl.data.data);
      setFallback(st.data.data.fallbackTechnicianIds);
    } catch (err) {
      setError(errorMessage(err, "Couldn't load asset settings."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveFallback = async () => {
    setSavingFallback(true);
    try {
      await api.put("/manufacturing/assets/settings", { fallbackTechnicianIds: fallback });
      setToast({ msg: "Fallback technicians saved", type: "success" });
    } catch (err) {
      setToast({ msg: errorMessage(err, "Couldn't save."), type: "error" });
    } finally {
      setSavingFallback(false);
    }
  };

  const toggle = (id) => setFallback((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  return (
    <PermissionGate permission={PERMISSIONS.MANUFACTURING_ASSET_MANAGE}>
      <div className="am-page">
        <Link className="back-link" to="/manufacturing/assets">← Back to Asset Management</Link>
        <div className="am-head">
          <div>
            <h1 className="am-title">Asset Management settings</h1>
            <p className="am-sub">Maintenance checklists, and who hears about breakdowns on machines with no technician of their own.</p>
          </div>
        </div>

        {loading ? (
          <div className="am-empty">Loading…</div>
        ) : error ? (
          <div className="am-empty"><strong>Something went wrong</strong>{error}</div>
        ) : (
          <>
            <div className="am-panel">
              <div className="am-panel-head">
                <h2 className="am-panel-title">Maintenance checklists</h2>
                <button type="button" className="am-btn am-btn-primary am-btn-sm" onClick={() => setModal({})}>+ New checklist</button>
              </div>
              {checklists.length === 0 ? (
                <p className="am-muted" style={{ margin: 0 }}>
                  No checklists yet. Create one — for example a weekly lubrication and safety check — and attach it to preventive maintenance schedules.
                </p>
              ) : (
                <div className="am-table-wrap">
                  <table className="am-table">
                    <thead>
                      <tr>
                        <th>Checklist</th>
                        <th className="am-num">Items</th>
                        <th className="am-num">Used by</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {checklists.map((c) => (
                        <tr key={c.id}>
                          <td>
                            <span className="am-strong">{c.name}</span>
                            {c.description && <div className="am-muted">{c.description}</div>}
                            <div className="am-muted">{(c.items || []).slice(0, 4).map((i) => i.label).join(" · ")}{c.items?.length > 4 ? " …" : ""}</div>
                          </td>
                          <td className="am-num">{c.items?.length || 0}</td>
                          <td className="am-num">{c._count?.pmSchedules || 0} schedule{c._count?.pmSchedules === 1 ? "" : "s"}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <button type="button" className="am-link-btn" onClick={() => setModal({ checklist: c })}>Edit</button>
                            {" · "}
                            <button type="button" className="am-link-btn danger" onClick={() => setConfirm(c)}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="am-panel">
              <div className="am-panel-head">
                <h2 className="am-panel-title">Fallback technicians</h2>
                <button type="button" className="am-btn am-btn-primary am-btn-sm" onClick={saveFallback} disabled={savingFallback}>
                  {savingFallback ? "Saving…" : "Save"}
                </button>
              </div>
              <p className="am-muted" style={{ marginTop: 0 }}>
                Alerted on chat when a machine with no technician breaks down, and reminded of its preventive maintenance. They can also acknowledge and resolve any breakdown.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
                {people.map((p) => (
                  <label key={p.id} className="am-check">
                    <input type="checkbox" checked={fallback.includes(p.id)} onChange={() => toggle(p.id)} />
                    {p.name}
                    {!p.reachable && <span className="am-muted">(no chat)</span>}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {modal && (
          <ChecklistModal
            checklist={modal.checklist}
            onClose={() => setModal(null)}
            onSaved={() => {
              setModal(null);
              setToast({ msg: modal.checklist ? "Checklist updated" : "Checklist created", type: "success" });
              load();
            }}
          />
        )}
        {confirm && (
          <ConfirmDialog
            title={`Delete "${confirm.name}"?`}
            message={
              confirm._count?.pmSchedules
                ? `${confirm._count.pmSchedules} schedule(s) use it and will be left without a checklist. Records already filled in keep their answers.`
                : "Records already filled in keep their answers."
            }
            confirmLabel="Delete checklist"
            confirmClass="btn-danger"
            onClose={() => setConfirm(null)}
            onConfirm={async () => {
              const target = confirm;
              setConfirm(null);
              try {
                await api.delete(`/manufacturing/assets/checklists/${target.id}`);
                setToast({ msg: "Checklist deleted", type: "success" });
                load();
              } catch (err) {
                setToast({ msg: errorMessage(err, "Couldn't delete the checklist."), type: "error" });
              }
            }}
          />
        )}
        {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
      </div>
    </PermissionGate>
  );
}
